import React, { useState } from 'react';
import classNames from 'classnames';
import Tabs, { TabPane } from 'rc-tabs';
import { ExternalLink, Lock, LockOpen } from '@saleshandy/icons';
import { useSelector } from 'react-redux';
import CopyButton from '../../../../../shared/components/copy-button';
import { Placement } from '../../../../../shared/design-system/components/overlay';
import { AiGradientStarIcon } from '../../../../../shared/svg/ai-gradient-star-icon';
import { AiSummaryNoData } from '../../../../../shared/svg/ai-summary-no-data';
import { Images } from '../../../../../shared/app-constants';
import { visibility } from '../../../../prospect/types';
import { sanitizeRichText } from '../../../../../shared/utils/sanitize-rich-text';
import { RootState } from '../../../../../store/root-reducer';
import {
  TaskerCallInsightsProps,
  TaskerCallInsightsTab,
} from './types/tasker-call-insights';
import {
  formatCallDate,
  formatCallTime,
  formatCallDuration,
  formatSummaryTimestamp,
  formatNoteTimestamp,
  getInitials,
  getCallDirection,
  getCallDirectionIcon,
  getOutcomeSentimentVariant,
  buildCallLogRedirectUrl,
} from './helper/helper';
import { convertHtmlToPlainTextForCopy } from '../../../../prospect/components/prospect-list/components/prospect-details/prospect-details-modal/email-list-tab/utils/helper';
import Attachment from '../../../../../shared/components/attachment';
import { mapCallNoteAttachmentsToAttachmentTypes } from './helper/map-call-note-attachments';

const TaskerCallInsights: React.FC<TaskerCallInsightsProps> = ({
  aiSummaries = [],
  callLogs = [],
  callNotes = [],
  prospectId,
  isAiSummaryPending = false,
}) => {
  const userTimezone = useSelector((state: RootState) => state?.home?.timeZone);

  const [activeTab, setActiveTab] = useState<TaskerCallInsightsTab>(
    'ai-summary',
  );

  const filteredAiSummaries = aiSummaries.filter(
    (summary) => summary.aiSummury,
  );
  const hasAiSummaries = filteredAiSummaries.length > 0;
  const hasCallLogs = callLogs.length > 0;
  const hasCallNotes = callNotes.length > 0;

  const renderAiSummaryTab = () => (
    <div
      className={`bs-tasker-call-insights__tab-panel ${
        activeTab === 'ai-summary' ? 'active-tab-ai-summary' : ''
      }`}
    >
      {isAiSummaryPending && (
        <div className="bs-tasker-call-insights__panel bs-tasker-call-insights__panel--filled">
          <p className="regular-2 gray-txt-11 bs-tasker-call-insights__body-text">
            Generating AI summary…
          </p>
        </div>
      )}

      {!isAiSummaryPending &&
        hasAiSummaries &&
        filteredAiSummaries.map((summary) => (
          <div
            key={summary.id}
            className="bs-tasker-call-insights__panel bs-tasker-call-insights__panel--filled bs-tasker-call-insights__panel--ai-summary"
          >
            <div className="bs-tasker-call-insights__panel-header">
              <span className="tasker-call-insights-panel-header-time">
                {formatSummaryTimestamp(summary.createdDate, userTimezone)}
              </span>
              <CopyButton
                text={summary.aiSummury || ''}
                copyTooltipText="Copy"
                resetDelay={1000}
                placement={Placement.Bottom}
                className="bs-tasker-call-insights__copy-btn"
              />
            </div>
            <div className="bs-tasker-call-insights__summary-body-scroll">
              <p className="regular-2 gray-txt-9 bs-tasker-call-insights__body-text">
                {summary.aiSummury}
              </p>
            </div>
          </div>
        ))}

      {!isAiSummaryPending && !hasAiSummaries && (
        <div className="bs-tasker-call-insights__empty">
          <AiSummaryNoData />
          <div className="bs-tasker-call-insights__empty-content">
            <p className="bs-tasker-call-insights__empty-title">
              No AI Summary Available
            </p>
            <p className="bs-tasker-call-insights__empty-caption">
              {hasCallLogs
                ? 'Call recording was turned off, so no AI summary could be generated for this call.'
                : 'No call logs are available for this prospect, so an AI summary could not be generated.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderCallLogsTab = () => {
    if (!hasCallLogs) {
      return (
        <div
          className={`bs-tasker-call-insights__tab-panel ${
            activeTab === 'call-logs' ? 'active-tab-ai-summary' : ''
          }`}
        >
          <div className="bs-tasker-call-insights__empty">
            <img
              src={Images.DialerCallLogsEmpty}
              alt="No call logs"
              height={80}
              width={80}
            />
            <div className="bs-tasker-call-insights__empty-content">
              <p className="bs-tasker-call-insights__empty-title">
                No Call Logs Available
              </p>
              <p className="bs-tasker-call-insights__empty-caption">
                There are no calls logged for this prospect yet. Once a call is
                made, it will appear here.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bs-tasker-call-insights__tab-panel bs-tasker-call-insights__tab-panel--list">
        <ul className="bs-tasker-call-insights__call-list">
          {callLogs.map((call) => {
            const direction = getCallDirection(call.type, call.isAnswered);
            const sentimentVariant = getOutcomeSentimentVariant(
              call.callOutcome?.sentiment,
            );
            const redirectUrl = buildCallLogRedirectUrl(call.id);

            return (
              <li key={call.id} className="bs-tasker-call-insights__call-item">
                <div className="bs-tasker-call-insights__call-left">
                  <span className="bs-tasker-call-insights__call-icon">
                    {getCallDirectionIcon(direction)}
                  </span>
                  <div className="bs-tasker-call-insights__call-meta">
                    <span className="bs-tasker-call-insights__call-date">
                      {formatCallDate(call.callStartedAt, userTimezone)}
                    </span>
                    <div className="bs-tasker-call-insights__call-sub">
                      <span className="bs-tasker-call-insights__call-time">
                        {formatCallTime(call.callStartedAt, userTimezone)}
                      </span>
                      <span className="bs-tasker-call-insights__call-dot" />
                      <span className="bs-tasker-call-insights__call-duration">
                        {formatCallDuration(call.callDuration)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bs-tasker-call-insights__call-right">
                  {call.callOutcome?.name && (
                    <div
                      className={classNames(
                        'bs-tasker-call-insights__call-outcome',
                        {
                          'bs-tasker-call-insights__call-outcome--positive':
                            sentimentVariant === 'positive',
                          'bs-tasker-call-insights__call-outcome--negative':
                            sentimentVariant === 'negative',
                        },
                      )}
                    >
                      <span className="bs-tasker-call-insights__call-outcome-label">
                        {call.callOutcome.name}
                      </span>
                    </div>
                  )}
                  <a
                    href={redirectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bs-tasker-call-insights__call-redirect"
                    aria-label="Open call in call logs"
                  >
                    <ExternalLink width={16} height={16} />
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderNotesTab = () => {
    if (!hasCallNotes) {
      return (
        <div
          className={`bs-tasker-call-insights__tab-panel ${
            activeTab === 'notes' ? 'active-tab-ai-summary' : ''
          }`}
        >
          <div className="bs-tasker-call-insights__empty">
            <img
              src={Images.NoProspectNote}
              alt="No notes"
              height={80}
              width={80}
            />
            <div className="bs-tasker-call-insights__empty-content">
              <p className="bs-tasker-call-insights__empty-title">
                No Notes Available
              </p>
              <p className="bs-tasker-call-insights__empty-caption">
                No notes have been added yet. Notes added during or after a call
                will show up here.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bs-tasker-call-insights__tab-panel bs-tasker-call-insights__tab-panel--list">
        <ul className="bs-tasker-call-insights__note-list">
          {callNotes.map((note) => {
            const authorName =
              [note.user?.firstName, note.user?.lastName]
                .filter(Boolean)
                .join(' ') || 'Unknown user';
            const initials = getInitials(
              note.user?.firstName,
              note.user?.lastName,
            );
            const isPublic = note.visibility === visibility.Public;
            const noteAttachments = mapCallNoteAttachmentsToAttachmentTypes(
              note.attachments,
            );

            return (
              <li key={note.id} className="bs-tasker-call-insights__note-item">
                <span className="bs-tasker-call-insights__note-avatar">
                  {initials}
                </span>

                <div className="bs-tasker-call-insights__note-body">
                  <div className="bs-tasker-call-insights__note-header">
                    <div className="bs-tasker-call-insights__note-header-left">
                      <span className="bs-tasker-call-insights__note-author">
                        {authorName}
                      </span>
                      <span className="bs-tasker-call-insights__note-timestamp">
                        {formatNoteTimestamp(note.createdAt, userTimezone)}
                      </span>
                    </div>

                    <div className="bs-tasker-call-insights__note-header-actions">
                      <CopyButton
                        text={
                          convertHtmlToPlainTextForCopy(note?.content) || ''
                        }
                        copyTooltipText="Copy"
                        resetDelay={1000}
                        placement={Placement.Bottom}
                        className="bs-tasker-call-insights__copy-btn"
                      />
                      <span className="bs-tasker-call-insights__note-visibility">
                        {isPublic ? (
                          <LockOpen
                            width={16}
                            height={16}
                            className="green-txt-16"
                          />
                        ) : (
                          <Lock
                            width={16}
                            height={16}
                            className="yellow-txt-13"
                          />
                        )}
                      </span>
                    </div>
                  </div>
                  <div
                    className="bs-tasker-call-insights__note-content"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichText(note?.content || ''),
                    }}
                  />
                  {noteAttachments.length > 0 && (
                    <div className="bs-tasker-call-insights__note-attachments">
                      {noteAttachments?.map((att) => (
                        <Attachment
                          key={att?.attachmentIdentifier}
                          attachment={att}
                          showAttachmentDeleteButton={false}
                          interactionDisabled={false}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className="bs-tasker-call-insights">
      <Tabs
        activeKey={activeTab}
        prefixCls="bs-tabs"
        className="bs-tabs-top bs-tabs-small bs-tasker-call-insights__tabs"
        destroyInactiveTabPane={false}
        onChange={(key) => setActiveTab(key as TaskerCallInsightsTab)}
        renderTabBar={(props, DefaultTabBar) => (
          <DefaultTabBar
            {...props}
            className={classNames(
              props.className,
              'bs-tasker-call-insights__tablist',
            )}
          />
        )}
      >
        <TabPane
          tab={
            <span
              className={classNames('bs-tasker-call-insights__tab', {
                'bs-tasker-call-insights__tab--active':
                  activeTab === 'ai-summary',
                'bs-tasker-call-insights__tab--ai-summary': true,
              })}
            >
              <AiGradientStarIcon
                width={16}
                height={16}
                className="bs-tasker-call-insights__tab-icon"
                activeType={activeTab === 'ai-summary'}
              />
              AI Summary
            </span>
          }
          key="ai-summary"
        >
          {renderAiSummaryTab()}
        </TabPane>

        <TabPane
          tab={
            <span
              className={classNames('bs-tasker-call-insights__tab', {
                'bs-tasker-call-insights__tab--active':
                  activeTab === 'call-logs',
              })}
            >
              Call Logs
            </span>
          }
          key="call-logs"
        >
          {renderCallLogsTab()}
        </TabPane>

        <TabPane
          tab={
            <span
              className={classNames('bs-tasker-call-insights__tab', {
                'bs-tasker-call-insights__tab--active': activeTab === 'notes',
              })}
            >
              Notes
            </span>
          }
          key="notes"
        >
          {renderNotesTab()}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default TaskerCallInsights;
